'use client';

import { useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { Container } from '@weareconceptstudio/core';

// Lazy load GSAP
let gsap = null;
const loadGSAP = async () => {
	if (!gsap) {
		const gsapModule = await import('gsap');
		gsap = gsapModule.default || gsapModule;
	}
	return gsap;
};

//* Styles
import HomeTemplateStyle from './style';

//* Components
import About from '../components/About';

// Critical above-the-fold component - load immediately
const Hero = dynamic(() => import('../components/Hero'), {
	loading: () => <div style={{ minHeight: '100vh', minWidth: '100vw', background: 'transparent' }}></div>,
	ssr: true,
});

// Below-the-fold components - lazy load with intersection observer for better performance
const ServicesHome = dynamic(() => import('../components/Services'), {
	ssr: false,
	loading: () => <div style={{ minHeight: '50vh' }}></div>,
});

const IndustriesSection = dynamic(() => import('@/sections/IndustriesSection'), {
	ssr: false,
	loading: () => <div style={{ minHeight: '30vh' }}></div>,
});

const NewsSection = dynamic(() => import('@/sections/NewsSection'), {
	ssr: false,
	loading: () => <div style={{ minHeight: '30vh' }}></div>,
});

const Subscribe = dynamic(() => import('@/sections/Subscribe'), {
	ssr: false,
	loading: () => <div style={{ minHeight: '20vh' }}></div>,
});

// const SuccessStoriesSection = dynamic(() => import('@/sections/SuccessStoriesSection'), {
// 	ssr: false,
// 	loading: () => <div style={{ minHeight: '40vh' }}></div>,
// });

const PartnersSection = dynamic(() => import('@/sections/PartnersSection'), {
	ssr: false,
	loading: () => <div style={{ minHeight: '20vh' }}></div>,
});

// const TestimonialsSection = dynamic(() => import('@/sections/TestimonialsSection'), {
// 	ssr: false,
// 	loading: () => <div style={{ minHeight: '30vh' }}></div>,
// });

const EventHome = dynamic(() => import('../components/Events'), {
	ssr: false,
	loading: () => <div style={{ minHeight: '30vh' }}></div>,
});

const FAQSection = dynamic(() => import('@/sections/FAQSection'), {
	ssr: false,
	loading: () => <div style={{ minHeight: '30vh' }}></div>,
});

const HomeTemplate = ({ pageData, latestStoriesData }) => {
	//! Refs
	const backgroundRef = useRef(null);
	const bottomSvgRef = useRef(null);

	useEffect(() => {
		if (!backgroundRef.current) return;

		// Load GSAP only when animation is needed
		loadGSAP().then((gsap) => {
			gsap.fromTo(
				bottomSvgRef.current,
				{ scaleY: 0 },
				{
					scaleY: 1,
					scrollTrigger: {
						start: 'top-=130% 80%',
						end: 'top+=150% 80%',
						// markers: true,
						trigger: backgroundRef.current,
						scrub: true,
					},
				}
			);
		});
	}, []);

	return (
		<HomeTemplateStyle>
			<div
				id='hero-section'
				className='wrap-hero-about'
			>
				<Hero
					featured_news={pageData.content?.featured_news}
					{...pageData.content.hero}
				/>

				<About
					{...pageData.content.about}
					infos={pageData.content.infos}
				/>

				<div
					ref={backgroundRef}
					className='background'
				>
					<svg
						className='svg-border'
						ref={bottomSvgRef}
						viewBox='0 0 100 25'
						preserveAspectRatio='none'
					>
						<path
							d='M0 0 Q 50 49.5 100 0'
							transformOrigin='0 0'
						/>
					</svg>
				</div>
			</div>

			<ServicesHome {...pageData.content.our_services} />

			<Container>
				<IndustriesSection
					title={pageData.content.industries.title}
					limit={6}
				/>

				<NewsSection
					title={pageData.content.news.title}
					data={pageData.news}
					params={{ latest: true }}
				/>

				<EventHome />

				<Subscribe />
			</Container>

			{/* <SuccessStoriesSection
				title={pageData.content.success_stories.title}
				data={latestStoriesData}
			/> */}

			<FAQSection items={pageData.faqs} />

			{/* <TestimonialsSection /> */}

			<Container
				full
				isSection
			>
				<PartnersSection
					title={pageData.content.partners.title}
					data={pageData.content.partners.items}
				/>
			</Container>
		</HomeTemplateStyle>
	);
};

export default HomeTemplate;
